/**
 * InstancedModelMeshComponent loads a GLTF model and creates instanced meshes for each child mesh.
 * Ideal for rendering large numbers of complex models efficiently. Manages instance matrices and
 * colors internally across all child meshes.
 *
 * Supports opt-in LOD via the `lods` property. LOD0 is the component's `modelUrl`; each
 * `GltfLODEntry` in `lods` points at a pre-authored lower-detail GLB. LOD GLBs may contain
 * fewer meshes than LOD0 (for merged far-distance assets); missing submeshes are skipped.
 */
import * as THREE from 'three';

import { AssetPath } from '../../storage/storageProvider.js';
import { ClassRegistry, EngineClass } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { isNode } from '../../utils/Utils.js';

import { BaseInstancedMeshComponent } from './BaseInstancedMeshComponent.js';

import type { BaseInstancedMeshComponentOptions, GeometryMaterialPair } from './BaseInstancedMeshComponent.js';
import type { GenericMaterialType } from '../../materials.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';
import type { ModelPath } from '../../utils/serialization/decorator.js';

/** One extra LOD tier for InstancedModelMeshComponent. LOD0 is the component's primary `modelUrl`. */
export interface ModelMeshLODEntry {
  modelUrl: string;
  /** Camera distance at which this LOD becomes the selected tier. */
  distance: number;
}

export interface InstancedModelMeshComponentOptions extends BaseInstancedMeshComponentOptions {
  modelUrl?: string;
  material?: GenericMaterialType;  // overrides the material for all instanced mesh slots (takes precedence over materials)
  materials?: Array<GenericMaterialType>;  // overrides materials per instanced mesh slot (by index)
  lods?: ModelMeshLODEntry[];
}

@EngineClass('InstancedModelMeshComponent')
export class InstancedModelMeshComponent extends BaseInstancedMeshComponent implements IAsyncLoadable {

  readonly [IAsyncLoadableTag] = true;

  @property({ required: true, description: 'Model URL' })
  public modelUrl: ModelPath | null = null;

  @property()
  public modelMaterial: GenericMaterialType | null = null;

  @property()
  public modelMaterials: Array<GenericMaterialType> | null = null;

  /** Extra LOD tiers. LOD0 is the component's `modelUrl`. */
  @property()
  public lods: ModelMeshLODEntry[] = [];

  // The loaded model that is NOT added to the hierarchy, meaning its transform is identity
  public modelTemplate: THREE.Object3D | null = null;


  private _asyncTaskWorker: AsyncTaskWorker = new AsyncTaskWorker();



  public override beginPlay(): void {
    super.beginPlay();

    if (this.modelUrl && !this.modelTemplate) {  // load the model only if not already loaded in postLoad
      this.loadModel();
    }
  }

  public override initialize(options?: InstancedModelMeshComponentOptions): void {
    super.initialize(options);

    this.modelUrl = options?.modelUrl ?? this.modelUrl;
    this.modelMaterial = options?.material ?? this.modelMaterial;
    this.modelMaterials = options?.materials ?? this.modelMaterials;
    this.lods = options?.lods ?? this.lods;

    // don't call loadModel here - wait until beginPlay
    // as this can be a very common pattern, and its gonna be tricky to "stop" an async load:
    //
    // const component = MyInstancedModelMeshComponent.create();  // where default modelUrl is set in constructor
    // component.modelUrl = 'some-other-model.glb';
  }

  public override postLoad(): void {
    super.postLoad();

    // load the model if modelUrl is set in saved data - scene
    // this is useful so that scene can be fully loaded with models before preStart of gameLoop is called
    if (this.modelUrl) {
      this.loadModel();
    }
  }

  /**
   * Gets the current model URL
   * @returns The model URL or null if not set
   */
  public getModelUrl(): string | null {
    return this.modelUrl;
  }

  /**
     * Gets the loaded THREE.Group model instance
     * @returns The loaded model group, or null if no model is loaded
     */
  public getModelTemplate(): THREE.Object3D | null {
    return this.modelTemplate;
  }

  /**
   * Sets the model URL and loads the new model
   * @param modelUrl The URL or AssetPath to the GLTF model
   * @param forceLoad If true, the model will be loaded even if the actor is not playing
   */
  public async setModelUrl(modelUrl: string, forceLoad: boolean = false): Promise<void> {
    this.modelUrl = modelUrl;

    if (forceLoad === true || this.getActor()?.isPlaying()) {
      await this.loadModel();
    }
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }

  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
    console.log('InstancedModelMeshComponent model load complete!!!');
  }

  private async loadModel(): Promise<void> {
    if (!this.modelUrl) return;

    if (this._asyncTaskWorker.isRunning()) {
      console.warn('InstancedModelMeshComponent: loadModel called while model is already loading');
      return;
    }

    console.log('Loading GLTF model for instanced mesh:', this.modelUrl);

    try {
      await this._asyncTaskWorker.runTask(async () => { await this._internalPerformModelLoad(); });
      for (const mesh of this.getInstancedMeshes()) {
        mesh.updateMatrixWorld(true);
      }
      this.refreshPhysics();
    } catch (error) {
      console.error('Failed to load GLTF model for instanced mesh:', error);
    }
  }

  /** Extract (geometry, material, geometryMatrix) from a loaded GLTF scene in traversal order. */
  private _extractPairsFromTemplate(template: THREE.Object3D): GeometryMaterialPair[] {
    const pairs: GeometryMaterialPair[] = [];
    const worldScale = new THREE.Vector3();
    template.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh && child.geometry) {
        // Clone geometry to avoid modifying the original
        const geometry = child.geometry.clone();

        // need to flip the material side when negative scaling is applied to the geometry
        // see https://github.com/mrdoob/three.js/issues/17361
        child.getWorldScale(worldScale);
        let material = child.material;
        if (worldScale.x * worldScale.y * worldScale.z < 0) {
          material = material.clone();
          if (material.side === THREE.BackSide) {
            material.side = THREE.FrontSide;
          } else {
            material.side = THREE.BackSide;
          }
        }

        const pairIndex = pairs.length;
        const overrideMaterial: GenericMaterialType = this.modelMaterial
          ?? (this.modelMaterials ? (this.modelMaterials[pairIndex] ?? material) : material);
        pairs.push({ geometry, material: overrideMaterial, geometryMatrix: child.matrixWorld.clone() });
      }
    });
    return pairs;
  }

  /** Extract ordered mesh names for parity validation. */
  private _extractMeshNamesFromTemplate(template: THREE.Object3D): string[] {
    const names: string[] = [];
    template.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh && child.geometry) {
        names.push(child.name ?? '');
      }
    });
    return names;
  }

  private async _internalPerformModelLoad(): Promise<void> {
    if (!this.modelUrl) return;

    // Convert string to AssetPath if needed
    const assetPath = AssetPath.fromString(this.modelUrl);

    // Use getLatest to prevent race conditions when model URL changes rapidly
    const gltf = await getLatest(this, 'model', () => resourceManager.loadModel(assetPath));
    if (!gltf) {
      // Either load failed or a newer load superseded this one
      if (!isNode() && gltf === null) {
        throw new Error(`Failed to load GLTF model: ${this.modelUrl}`);
      }
      return;
    }

    this.modelTemplate = gltf.scene;

    // Extract LOD0 pairs.
    const lod0Pairs = this._extractPairsFromTemplate(this.modelTemplate);

    console.log(`Found ${lod0Pairs.length} meshes in GLTF model (LOD0)`);

    // If no extra LODs requested, preserve the existing single-LOD code path.
    if (!this.lods || this.lods.length === 0) {
      this.populateInstances(lod0Pairs);
      return;
    }

    // Eagerly load every extra LOD GLB in parallel.
    const sortedLods = [...this.lods]
      .filter(l => l.distance > 0 && typeof l.modelUrl === 'string' && l.modelUrl.length > 0)
      .sort((a, b) => a.distance - b.distance);

    const lodLoadResults = await Promise.all(sortedLods.map(async (entry) => {
      try {
        const path = AssetPath.fromString(entry.modelUrl);
        const loaded = await resourceManager.loadModel(path);
        return { entry, scene: loaded?.scene ?? null };
      } catch (err) {
        console.error(`InstancedModelMeshComponent: failed to load LOD GLB ${entry.modelUrl}`, err);
        return { entry, scene: null as THREE.Object3D | null };
      }
    }));

    // Validate loaded LOD assets. Keep this permissive: only hard load failures
    // fall back to LOD0-only; structural mismatches are accepted as-is.
    const lodPairs: GeometryMaterialPair[][] = [lod0Pairs];
    const distances: number[] = [0];
    for (const { entry, scene } of lodLoadResults) {
      if (!scene) {
        console.error(`InstancedModelMeshComponent: LOD '${entry.modelUrl}' failed to load; falling back to LOD0-only`);
        lodPairs.length = 1;
        distances.length = 1;
        break;
      }
      const pairs = this._extractPairsFromTemplate(scene);
      lodPairs.push(pairs);
      distances.push(entry.distance);
    }


    this.populateInstances(lodPairs, distances);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    if (this.modelUrl) {
      this.loadModel();
    }
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    // force load the model if the model URL changes, and do this before super.onEditorPropertyChanged(), as super will call populateInstances() which refreshes the instances
    if (path === 'modelUrl') {
      this.setModelUrl(value, true);
    } else if (path === 'lods') {
      // Re-run the load pipeline to fetch any new LOD GLBs.
      if (this.modelUrl) this.loadModel();
    }
    super.onEditorPropertyChanged(path, value, result);
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_InstancedGLTF';
  }
}

ClassRegistry.addClassesToRedirect({
  'ENGINE.InstancedGltfMeshComponent': 'ENGINE.InstancedModelMeshComponent',
});

/**
 * @deprecated Use {@link InstancedModelMeshComponent} instead.
 */
export const InstancedGltfMeshComponent = InstancedModelMeshComponent;

/**
 * @deprecated Use {@link InstancedModelMeshComponent} instead.
 */
export type InstancedGltfMeshComponent = InstancedModelMeshComponent;

/**
 * @deprecated Use {@link InstancedModelMeshComponentOptions} instead.
 */
export type InstancedGltfMeshComponentOptions = InstancedModelMeshComponentOptions;

/**
 * @deprecated Use {@link ModelMeshLODEntry} instead.
 */
export type GltfLODEntry = ModelMeshLODEntry;
